<template>
  <!-- RANDOM -->
  <div
    v-if="props.display === PairingGroupDisplay.Random"
    class="flex flex-wrap gap-4"
    :class="isPreviewMode ? 'justify-center' : 'justify-start'">
    <div v-for="(option, index) in options" :key="index" class="flex">
      <div class="flex grow gap-4">
        <slot name="option" :item="option" :index="index"></slot>
        <slot name="match" :item="matches[index]" :index="index"></slot>
      </div>
    </div>

    <div
      v-if="hasSlot('add-pairing') && props.options.length < maxPairs"
      class="relative ms-2">
      <slot name="add-pairing"></slot>
    </div>
  </div>
  <!-- COLUMNS -->
  <div v-else class="flex grow">
    <div class="flex grow flex-col">
      <div class="flex gap-8 w-full">
        <div class="w-1/2 h-full">
          <HeadLine
            :id="`${id}_title_options`"
            v-model="modelOptionsName"
            :closable="false"
            :editable="!isPreviewMode"
            :placeholder="`${
              direction === 'ltr'
                ? 'Group A'
                : t('blocksSettings.pairing.group_a')
            }`"
            visual-boundary="border"
            :options="{
              defaultNode: 'headline',
              fontSize: '12px',
              maxLength: 50,
              sizeBreakpoint: 60,
              textAlign: 'center',
            }"
            class="h-full"/>
        </div>
        <div class="w-1/2 h-full">
          <HeadLine
            :id="`${id}_title_match`"
            v-model="modelMatchesName"
            :closable="false"
            :editable="!isPreviewMode"
            :placeholder="`${
              direction === 'ltr'
                ? 'Group B'
                : t('blocksSettings.pairing.group_b')
            }`"
            visual-boundary="border"
            :options="{
              defaultNode: 'headline',
              fontSize: '12px',
              maxLength: 50,
              sizeBreakpoint: 60,
              textAlign: 'center',
            }"
            class="h-full"/>
        </div>
      </div>
      <div class="flex gap-8 w-full">
        <div class="w-1/2">
          <div class="flex flex-wrap gap-4 mt-4">
            <div v-for="(option, index) in options" :key="index" class="flex">
              <div class="flex grow justify-center gap-4">
                <slot name="option" :item="option" :index="index"></slot>
              </div>
            </div>
          </div>
        </div>
        <div class="w-1/2">
          <div class="flex flex-wrap gap-4 mt-4">
            <div v-for="(match, index) in matches" :key="index" class="flex">
              <div class="flex grow gap-4">
                <slot name="match" :item="match" :index="index"></slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="hasSlot('add-pairing') && props.options.length < maxPairs"
      class="relative ms-2 mt-10">
      <slot name="add-pairing"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSlots, type PropType, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { PairingGroupDisplay, type PairingItemData } from '@amit/types';
import { useVModel } from '@vueuse/core';
import { HeadLine } from '../../index.ts';

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  display: {
    type: String as PropType<PairingGroupDisplay>,
    default: PairingGroupDisplay.Columns,
  },
  optionsName: {
    type: String,
    default: '',
  },
  matchesName: {
    type: String,
    default: '',
  },
  options: {
    type: Array as PropType<Array<PairingItemData>>,
    default: () => [],
  },
  matches: {
    type: Array as PropType<Array<PairingItemData>>,
    default: () => [],
  },
  isPreviewMode: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const slots = useSlots();
const hasSlot = name => !!slots[name];
const maxPairs = 8;
const direction = inject('direction');

const emit = defineEmits(['change-options-name', 'change-matches-name']);
const modelOptionsName = useVModel(props, 'optionsName', emit, {
  eventName: 'change-options-name',
});
const modelMatchesName = useVModel(props, 'matchesName', emit, {
  eventName: 'change-matches-name',
});
</script>

<style scoped>
:deep(.headline) {
  @apply h-full;
}
:deep(.tiptap-placeholder) {
  height: 18px;
  font-size: 14px;
}

:deep(.tiptap-placeholder) p {
  font-size: 14px;
}
:deep(.form-item) {
  @apply justify-center;
}
</style>
